'use client';

import React, { useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Button } from './ui/Button';
import { useWillStore } from '@/lib/store';
import { createClient } from '@/lib/supabase/client';
import { SaveProgressModal } from './SaveProgressModal';
import { ExitConfirmationModal } from './ExitConfirmationModal';
import { LogOut } from 'lucide-react';

export const Navbar = () => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const { reset, isAuthenticated, isEligible, isCapable, isSuitable } = useWillStore();

  const isEligibilityFlow =
    pathname?.startsWith('/write-a-will-online') &&
    !isAuthenticated &&
    (isEligible === null || isCapable === null || isSuitable === null);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // During eligibility questions, go straight home without prompting to save
    if (isEligibilityFlow) {
      router.push('/');
      return;
    }

    // If in drafting section and not authenticated, show exit confirmation
    if (pathname?.startsWith('/write-a-will-online') && !isAuthenticated) {
      setShowExitModal(true);
      return;
    }

    if (isAuthenticated) {
      router.push('/');
    } else {
      setShowSaveModal(true);
    }
  };

  const handleExitConfirm = () => {
    // Clear progress and go home
    reset();
    setShowExitModal(false);
    router.push('/');
  };

  const handleExitSave = () => {
    // Open save modal
    setShowExitModal(false);
    setShowSaveModal(true);
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    const supabase = createClient();

    // Force save current state before signing out
    const { willId, isAuthenticated, data, currentStep, completedSteps } = useWillStore.getState();

    if (willId && isAuthenticated) {
      try {
        const updatePayload: any = {
          will_data: { ...data },
          current_step: currentStep,
          completed_steps: completedSteps,
          updated_at: new Date().toISOString(),
        };

        if (data.personalDetails?.fullName) {
          updatePayload.full_name = data.personalDetails.fullName;
        }

        // Add a timeout to the save operation to prevent hanging
        const savePromise = supabase
          .from('wills')
          .update(updatePayload)
          .eq('id', willId);

        await Promise.race([
          savePromise,
          new Promise((resolve) => setTimeout(resolve, 3000))
        ]);

        console.log('State saved (or timed out) before sign out');
      } catch (err) {
        console.error('Error saving before sign out:', err);
      }
    }

    // Hide any modals immediately to avoid flicker
    setShowExitModal(false);
    setShowSaveModal(false);

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }

    reset(); // Optional: clear local store as well
    setIsSigningOut(false);
    router.push('/');
  };

  return (
    <>
      <nav className="w-full bg-white/80 backdrop-blur-md border-b border-foreground/10 z-50">
        <div className="container mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-3">
          <button
            onClick={handleLogoClick}
            className="font-display text-xl sm:text-2xl tracking-tight hover:opacity-80 transition-opacity text-primary leading-none"
          >
            WILLDONE
          </button>

          <div className="flex gap-2">
            {isEligibilityFlow ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => router.push('/sign-in')}
                className="uppercase text-xs font-bold tracking-widest"
              >
                Login
              </Button>
            ) : (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const { setStep } = useWillStore.getState();
                    setStep(5);
                  }}
                  className="uppercase text-xs font-bold tracking-widest shadow-md shadow-primary/25"
                >
                  My Will
                </Button>

                {isAuthenticated ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowExitModal(true)}
                    className="uppercase text-xs font-bold tracking-widest"
                  >
                    <LogOut className="w-3 h-3 mr-2" /> Exit
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveModal(true)}
                    className="uppercase text-xs font-bold tracking-widest"
                  >
                    <LogOut className="w-3 h-3 mr-2" /> Exit
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      <SaveProgressModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        mode="save"
        onSuccess={() => {
          // Redirect to homepage after saving progress
          router.push('/');
        }}
      />

      <ExitConfirmationModal
        isOpen={showExitModal}
        title={isAuthenticated ? "Are you Sure?" : "Save your progress?"}
        description={isAuthenticated ? "This will log you out and clear all your browsing data. You can pick up from where you left off any time." : "Would you like to save your progress before you go?"}
        primaryCta={isAuthenticated ? "Yes, sign out" : "Save Progress"}
        secondaryCta={isAuthenticated ? "Cancel" : "Leave Without Saving"}
        primaryAction={isAuthenticated ? 'confirm' : 'save'}
        secondaryAction={isAuthenticated ? 'close' : 'confirm'}
        onClose={() => setShowExitModal(false)}
        onConfirmExit={isAuthenticated ? handleSignOut : handleExitConfirm}
        onSave={handleExitSave}
        isLoading={isSigningOut}
      />
    </>
  );
};
